import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive, Beaker, Check, Copy, Loader2, Play, RefreshCw, Save, Trash2, X,
} from 'lucide-react';
import {
  emptyPlaygroundSpec,
  isBaselineSpec,
  isRetrieveMetrics,
  type PlaygroundAgentInfo,
  type PlaygroundCaseItem,
  type PlaygroundExpectation,
  type PlaygroundSpec,
} from '@repo/shared-types';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { RunPhaseHeader, RunTimelineView } from '../../components/run/RunActivity';
import { RunCostChip, runUsageOf } from '../../components/run/RunCost';
import { ContentPicker, contentProblem, type ContentSource } from '../../components/playground/ContentPicker';
import { ExpectationEditor } from '../../components/playground/ExpectationEditor';
import {
  AnswerPanel,
  DecisionPanel,
  GradePanel,
  RetrieveRunMetrics,
  RunMetrics,
} from '../../components/playground/RunResult';
import { SpecEditor } from '../../components/playground/SpecEditor';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Drawer } from '../../components/ui/drawer';
import { Input, Textarea } from '../../components/ui/input';
import { useLiveRunStream } from '../../components/playground/useLiveRunStream';
import { useProjectTree } from '../../lib/project-tree';
import { formatSource, useSourceLabels } from '../../lib/source-label';
import { cn } from '../../lib/utils';
import {
  api,
  type PlaygroundCase,
  type PlaygroundConfig,
  type PlaygroundRun,
  type PlaygroundResult,
  type PlaygroundRetrieveResult,
  type PlaygroundRunDetail,
} from '../../lib/api';

type Tab = 'bench' | 'runs' | 'cases' | 'configs';

const TABS: Array<[Tab, string]> = [
  ['bench', 'Bench'],
  ['runs', 'Runs'],
  ['cases', 'Cases'],
  ['configs', 'Configs'],
];

/**
 * The agent playground.
 *
 * Four surfaces over one idea: run the shipped agent with its tools and prompt substituted, against
 * real content, and keep every run. Bench is where a change is tried; Runs is the audit trail; Cases
 * is the test library; Configs are the rosters worth keeping.
 *
 * Everything here is read-only against the knowledge base. The placement phase's whole tool roster
 * reads, and the phase that edits pages is never reached — which is what makes running it against
 * the live hierarchy the right thing to do rather than a risk to be managed. It is stated on the
 * page, not just in a comment, because the first question anyone sensible asks of a button labelled
 * "run the ingestion agent" is what it is about to write.
 */
export default function AdminPlayground() {
  const [tab, setTab] = useState<Tab>('bench');
  const [agents, setAgents] = useState<PlaygroundAgentInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [cases, setCases] = useState<PlaygroundCase[]>([]);
  const [configs, setConfigs] = useState<PlaygroundConfig[]>([]);
  const [runs, setRuns] = useState<PlaygroundRun[]>([]);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const loadCases = useCallback(() => api.playground.listCases().then(setCases).catch(() => {}), []);
  const loadConfigs = useCallback(() => api.playground.listConfigs().then(setConfigs).catch(() => {}), []);
  const loadRuns = useCallback(
    () => api.playground.listRuns({ limit: 60 }).then(setRuns).catch(() => {}),
    [],
  );

  useEffect(() => {
    api.playground.agents().then(setAgents).catch((e: Error) => setError(e.message));
    void loadCases();
    void loadConfigs();
    void loadRuns();
  }, [loadCases, loadConfigs, loadRuns]);

  /**
   * While anything is in flight, poll the list.
   *
   * The runs themselves stream — the drawer attaches an EventSource — but the *list* has to stay
   * alive without one connection per row, which is exactly what the denormalized `phase`/`activity`
   * columns exist for. Stops the moment nothing is moving.
   */
  const live = runs.some((run) => run.status === 'QUEUED' || run.status === 'PROCESSING');
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => void loadRuns(), 3000);
    return () => clearInterval(timer);
  }, [live, loadRuns]);

  const [agentKey, setAgentKey] = useState<PlaygroundAgentInfo['agent'] | null>(null);
  const agent = agents?.find((one) => one.agent === agentKey) ?? agents?.[0] ?? null;
  // Cases and configs belong to one agent, so each surface shows only the selected agent's; the
  // run list keeps everything, labelled, since a comparison across agents is still a history.
  const agentCases = agent ? cases.filter((one) => one.agent === agent.agent) : [];
  const agentConfigs = agent ? configs.filter((one) => one.agent === agent.agent) : [];

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <AdminSidebar />

      <div className="h-full flex-1 overflow-y-auto pl-56">
        <div className="mx-auto max-w-6xl px-7 py-7">
          <div className="mb-5 flex items-start gap-3">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-ink">Agent playground</h1>
              <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-muted">
                Runs a shipped agent with its tool roster and prompt substituted, against real
                content, and keeps every run. Nothing is written: both agents here only read — the
                placement phase never reaches the phase that edits pages, and the ask loop has no
                write tool — so a rehearsal reads the live store and leaves it exactly as it found it.
              </p>
            </div>
            <button
              onClick={() => {
                void loadRuns();
                void loadCases();
                void loadConfigs();
              }}
              className="ml-auto text-ink-faint transition-colors hover:text-ink"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>

          {agents && agents.length > 1 && (
            <div className="mb-4 flex gap-1 rounded-lg bg-surface-muted p-0.5 w-fit">
              {agents.map((one) => (
                <button
                  key={one.agent}
                  onClick={() => setAgentKey(one.agent)}
                  title={one.blurb}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-[11px] font-medium transition-colors',
                    agent?.agent === one.agent ? 'bg-card text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
                  )}
                >
                  {one.label}
                </button>
              ))}
            </div>
          )}

          <div className="mb-5 flex gap-1 border-b border-border">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors',
                  tab === key
                    ? 'border-orange-500 text-ink'
                    : 'border-transparent text-ink-muted hover:text-ink',
                )}
              >
                {label}
                {key === 'runs' && live && (
                  <span className="ml-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-orange-500" />
                )}
              </button>
            ))}
          </div>

          {error && (
            <p className="mb-4 rounded-md border border-danger-ring bg-danger-bg px-3 py-2 text-[11px] text-danger-text">
              {error}
            </p>
          )}

          {!agents && !error && <Loading />}

          {agent && tab === 'bench' && (
            <Bench
              key={agent.agent}
              agent={agent}
              cases={agentCases}
              configs={agentConfigs}
              onQueued={(ids) => {
                void loadRuns();
                setOpenRunId(ids[0] ?? null);
                setTab('runs');
              }}
              onConfigSaved={loadConfigs}
              onCaseSaved={loadCases}
            />
          )}

          {agent && tab === 'runs' && (
            <RunList runs={runs} onOpen={setOpenRunId} onChanged={loadRuns} />
          )}

          {agent && tab === 'cases' && (
            <CaseList agent={agent} cases={agentCases} onChanged={loadCases} />
          )}

          {agent && tab === 'configs' && (
            <ConfigList configs={agentConfigs} onChanged={loadConfigs} />
          )}
        </div>
      </div>

      <RunDrawer runId={openRunId} onClose={() => setOpenRunId(null)} onChanged={loadRuns} />
    </div>
  );
}

// ─── Bench ────────────────────────────────────────────────────────────────────

function Bench({
  agent,
  cases,
  configs,
  onQueued,
  onConfigSaved,
  onCaseSaved,
}: {
  agent: PlaygroundAgentInfo;
  cases: PlaygroundCase[];
  configs: PlaygroundConfig[];
  onQueued: (runIds: string[]) => void;
  onConfigSaved: () => void;
  onCaseSaved: () => void;
}) {
  const { allProjects } = useProjectTree();
  const [spec, setSpec] = useState<PlaygroundSpec>(emptyPlaygroundSpec());
  const [configId, setConfigId] = useState<string | null>(null);
  const [content, setContent] = useState<ContentSource>({ kind: 'inline', label: '', items: [] });
  const [expectation, setExpectation] = useState<PlaygroundExpectation | null>(null);
  const [repeat, setRepeat] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState<'config' | 'case' | null>(null);

  const problem = contentProblem(content);
  const baseline = isBaselineSpec(spec);

  const inlineItems: PlaygroundCaseItem[] = content.kind === 'inline' ? content.items : [];

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.playground.startRuns({
        agent: agent.agent,
        ...(configId ? { configId } : { spec }),
        ...(content.kind === 'cases'
          ? { caseIds: content.caseIds }
          : { items: content.items, label: content.label || undefined }),
        // An inline run can still be graded without saving a case — the expectation is snapshotted
        // onto the run like everything else, so a scratch experiment is judged the same way a suite is.
        ...(content.kind === 'inline' && expectation ? { expectation } : {}),
        ...(repeat > 1 ? { repeat } : {}),
      });
      onQueued(result.runs.map((one) => one.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-5">
      {/* ── Configuration ────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Beaker size={14} className="text-ink-faint" />
          <h2 className="text-xs font-semibold text-ink">{agent.label}</h2>
          {baseline ? (
            <Badge variant="muted">baseline — exactly what ships</Badge>
          ) : (
            <Badge variant="info">modified</Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <select
              value={configId ?? ''}
              onChange={(e) => {
                const id = e.target.value || null;
                setConfigId(id);
                const found = configs.find((one) => one.id === id);
                if (found) setSpec(found.spec);
                if (!id) setSpec(emptyPlaygroundSpec());
              }}
              className="rounded-md border border-border-strong bg-card px-2 py-1 text-[11px] text-ink"
            >
              <option value="">Unsaved</option>
              {configs.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSaveOpen('config')}
              disabled={baseline}
              title={baseline ? 'Nothing to save — this is the shipped agent' : 'Save this configuration'}
            >
              <Save size={12} />
            </Button>
          </div>
        </div>

        <p className="mb-4 rounded-md border border-border-subtle bg-surface-subtle px-2.5 py-1.5 text-[10px] leading-relaxed text-ink-muted">
          {agent.safety}
        </p>

        <SpecEditor
          agent={agent}
          spec={spec}
          disabled={busy}
          onChange={(next) => {
            setSpec(next);
            // Editing a loaded config detaches from it rather than silently diverging under its
            // name: the run would otherwise be attributed to a config it did not use.
            if (configId) setConfigId(null);
          }}
        />
      </div>

      {/* ── Content and run ──────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-2.5 text-xs font-semibold text-ink">{agent.input === 'question' ? 'Question' : 'Content'}</h2>
          <ContentPicker cases={cases} value={content} onChange={setContent} disabled={busy} mode={agent.input} />
        </div>

        {content.kind === 'inline' && inlineItems.length > 0 && (
          <details className="rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer text-xs font-semibold text-ink">
              Expect something of it
              {expectation && <Badge variant="info" className="ml-2">set</Badge>}
            </summary>
            <p className="mb-2.5 mt-2 text-[10px] leading-relaxed text-ink-faint">
              Optional. Without it the run is measured but not judged — which is enough to see what
              happened, and not enough to tell whether a change helped.
            </p>
            <ExpectationEditor
              projects={allProjects}
              value={expectation}
              onChange={setExpectation}
              disabled={busy}
              mode={agent.input}
            />
          </details>
        )}

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2.5 flex items-center gap-2">
            <label className="text-[10px] font-medium text-ink-secondary">Repeat</label>
            <select
              value={repeat}
              onChange={(e) => setRepeat(Number(e.target.value))}
              disabled={busy}
              className="rounded-md border border-border-strong bg-card px-2 py-1 text-[11px] text-ink"
            >
              {[1, 2, 3, 5].map((n) => (
                <option key={n} value={n}>
                  {n}×
                </option>
              ))}
            </select>
            <span className="text-[10px] leading-relaxed text-ink-faint">
              {agent.input === 'question' ? 'the ask loop' : 'placement'} is not deterministic — one run is an anecdote
            </span>
          </div>

          <Button variant="primary" className="w-full" onClick={run} disabled={busy || Boolean(problem)}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {content.kind === 'cases' && content.caseIds.length > 1
              ? `Run ${content.caseIds.length * repeat} rehearsals`
              : repeat > 1
                ? `Run ${repeat} rehearsals`
                : 'Run'}
          </Button>

          {problem && <p className="mt-2 text-[10px] text-ink-faint">{problem}</p>}
          {error && <p className="mt-2 text-[10px] text-danger-text">{error}</p>}

          {content.kind === 'inline' && inlineItems.length > 0 && (
            <button
              onClick={() => setSaveOpen('case')}
              className="mt-2 w-full text-[10px] text-ink-faint transition-colors hover:text-ink"
            >
              Save this content as a case
            </button>
          )}
        </div>
      </div>

      {saveOpen === 'config' && (
        <SaveConfigDialog
          agent={agent}
          spec={spec}
          onClose={() => setSaveOpen(null)}
          onSaved={(saved) => {
            setConfigId(saved.id);
            onConfigSaved();
            setSaveOpen(null);
          }}
        />
      )}

      {saveOpen === 'case' && (
        <SaveCaseDialog
          agent={agent}
          items={inlineItems}
          expectation={expectation}
          defaultName={content.kind === 'inline' ? content.label : ''}
          onClose={() => setSaveOpen(null)}
          onSaved={() => {
            onCaseSaved();
            setSaveOpen(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Runs ─────────────────────────────────────────────────────────────────────

function RunList({
  runs,
  onOpen,
  onChanged,
}: {
  runs: PlaygroundRun[];
  onOpen: (id: string) => void;
  onChanged: () => void;
}) {
  const [batchFilter, setBatchFilter] = useState<string | null>(null);
  const shown = batchFilter ? runs.filter((run) => run.batchId === batchFilter) : runs;

  if (runs.length === 0) {
    return <Empty>Nothing has been rehearsed yet.</Empty>;
  }

  return (
    <div>
      {batchFilter && (
        <button
          onClick={() => setBatchFilter(null)}
          className="mb-2 flex items-center gap-1 text-[10px] text-orange-500 hover:text-orange-700"
        >
          <X size={10} />
          Showing one batch — clear
        </button>
      )}

      <div className="space-y-1">
        {shown.map((run) => (
          <RunRow
            key={run.id}
            run={run}
            onOpen={() => onOpen(run.id)}
            onBatch={() => setBatchFilter(run.batchId)}
            onDeleted={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

function RunRow({
  run,
  onOpen,
  onBatch,
  onDeleted,
}: {
  run: PlaygroundRun;
  onOpen: () => void;
  onBatch: () => void;
  onDeleted: () => void;
}) {
  const moving = run.status === 'QUEUED' || run.status === 'PROCESSING';

  return (
    <div className="group flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:bg-surface-subtle">
      <button onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
        <StatusDot run={run} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[11px] font-medium text-ink">{run.label}</span>
            {run.config ? (
              <Badge variant="info">{run.config.name}</Badge>
            ) : isBaselineSpec(run.configSnapshot) ? (
              <Badge variant="muted">baseline</Badge>
            ) : (
              <Badge variant="default">unsaved config</Badge>
            )}
          </div>
          <p className="mt-0.5 truncate text-[10px] text-ink-faint">
            {moving
              ? run.activity ?? 'queued'
              : run.errorMessage
                ? run.errorMessage
                : isRetrieveMetrics(run.metrics)
                  ? `${run.metrics.answered ? 'answered' : 'fallback answered'} · ${run.metrics.cited.length} cited · ${run.metrics.visitedCount} opened`
                  : run.metrics
                    ? `${run.metrics.destinationCount} project${run.metrics.destinationCount === 1 ? '' : 's'} · ${run.metrics.claimedPct}% claimed · ${run.metrics.assignedPct}% assigned${run.metrics.rejectedCount > 0 ? ` · ${run.metrics.rejectedCount} under the floor` : ''}`
                    : '—'}
          </p>
        </div>
      </button>

      {run.metrics && !isRetrieveMetrics(run.metrics) && run.metrics.assignedPct > 100.5 && (
        <span
          title="Two or more projects were handed the same sentences"
          className="flex-shrink-0 rounded bg-warning-bg px-1.5 py-0.5 text-[9px] font-medium text-warning-text"
        >
          overlap
        </span>
      )}

      {run.passed !== null && (
        <Badge variant={run.passed ? 'success' : 'danger'}>{run.passed ? 'pass' : 'fail'}</Badge>
      )}

      <RunCostChip usage={runUsageOf(run)} className="flex-shrink-0" />

      <span className="w-14 flex-shrink-0 text-right text-[10px] tabular-nums text-ink-faint">
        {new Date(run.queuedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>

      <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {run.batchId && (
          <button onClick={onBatch} title="Show this batch" className="text-ink-faint hover:text-ink">
            <Copy size={11} />
          </button>
        )}
        <button
          onClick={async () => {
            await api.playground.deleteRun(run.id);
            onDeleted();
          }}
          title="Delete"
          className="text-ink-faint hover:text-danger-text"
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

function StatusDot({ run }: { run: PlaygroundRun }) {
  if (run.status === 'QUEUED' || run.status === 'PROCESSING') {
    return <span className="h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full bg-orange-500" />;
  }
  if (run.status === 'FAILED') return <X size={12} className="flex-shrink-0 text-danger-text" />;
  return <Check size={12} className="flex-shrink-0 text-emerald-500" />;
}

function RunDrawer({
  runId,
  onClose,
  onChanged,
}: {
  runId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [run, setRun] = useState<PlaygroundRunDetail | null>(null);
  const [tab, setTab] = useState<'result' | 'activity' | 'config' | 'content'>('result');
  const sourceLabels = useSourceLabels();

  useEffect(() => {
    setRun(null);
    if (!runId) return;
    let cancelled = false;
    const load = () =>
      api.playground
        .getRun(runId)
        .then((detail) => {
          if (cancelled) return;
          setRun(detail);
          if (detail.status === 'QUEUED' || detail.status === 'PROCESSING') setTab('activity');
        })
        .catch(() => {});
    void load();
    // Refetches only while it is moving, and stops the moment it is not — the timeline itself comes
    // over SSE, so this is only here to pick up the final row.
    const timer = setInterval(() => {
      if (run?.status === 'COMPLETED' || run?.status === 'FAILED') return;
      void load();
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      onChanged();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  return (
    <Drawer open={Boolean(runId)} onClose={onClose} title={run?.label ?? 'Rehearsal'} className="w-[640px]">
      {!run ? (
        <Loading />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {run.config ? <Badge variant="info">{run.config.name}</Badge> : null}
            {isBaselineSpec(run.configSnapshot) && <Badge variant="muted">baseline</Badge>}
            {run.passed !== null && (
              <Badge variant={run.passed ? 'success' : 'danger'}>{run.passed ? 'passed' : 'failed'}</Badge>
            )}
            <RunCostChip usage={runUsageOf(run)} />
            <span className="ml-auto text-[10px] text-ink-faint">
              {new Date(run.queuedAt).toLocaleString()}
            </span>
          </div>

          {run.errorMessage && (
            <p className="rounded-md border border-danger-ring bg-danger-bg px-2.5 py-1.5 text-[11px] text-danger-text">
              {run.errorMessage}
            </p>
          )}

          <div className="flex gap-1 border-b border-border">
            {(
              [
                ['result', 'Result'],
                ['activity', 'Activity'],
                ['config', 'Config'],
                ['content', 'Content'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={cn(
                  '-mb-px border-b-2 px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                  tab === key ? 'border-orange-500 text-ink' : 'border-transparent text-ink-muted hover:text-ink',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'result' && (
            <div className="space-y-4">
              {isRetrieveMetrics(run.metrics) ? (
                <RetrieveRunMetrics metrics={run.metrics} />
              ) : run.metrics ? (
                <RunMetrics metrics={run.metrics} />
              ) : (
                <Empty>Nothing measured yet.</Empty>
              )}
              {run.grade && (
                <section>
                  <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                    Expectations
                  </h3>
                  <GradePanel grade={run.grade} />
                </section>
              )}
              {run.result && (
                <section>
                  <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                    {run.agent === 'KB_RETRIEVE' ? 'Answer' : 'Decision'}
                  </h3>
                  {run.agent === 'KB_RETRIEVE' ? (
                    <AnswerPanel result={run.result as PlaygroundRetrieveResult} />
                  ) : (
                    <DecisionPanel result={run.result as PlaygroundResult} />
                  )}
                </section>
              )}
            </div>
          )}

          {tab === 'activity' && <RunActivityTab run={run} />}

          {tab === 'config' && <ConfigSnapshot run={run} />}

          {tab === 'content' && (
            <div className="space-y-2">
              {run.caseSnapshot.map((item, index) => (
                <div key={index} className="rounded-lg border border-border bg-surface-subtle p-3">
                  <div className="mb-1.5 flex items-center gap-2 text-[10px] text-ink-faint">
                    <span className="font-medium text-ink-secondary">
                      {item.title ?? formatSource(item.source, sourceLabels)}
                    </span>
                    <span>{item.content.length.toLocaleString()} chars</span>
                    {item.sourceTimestamp && <span>{new Date(item.sourceTimestamp).toLocaleDateString()}</span>}
                  </div>
                  <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed text-ink-muted">
                    {item.content}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

/** The live trail, on the same wire and the same components ingestion and automation runs use. */
function RunActivityTab({ run }: { run: PlaygroundRunDetail }) {
  const { timeline, live, loading, headline } = useLiveRunStream(run.id, run.status);

  return (
    <div className="space-y-3">
      <RunPhaseHeader timeline={timeline} headline={headline} live={live} />
      <RunTimelineView timeline={timeline} live={live} loading={loading} />

      {run.steps && run.steps.length > 0 && (
        <details>
          <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
            The working — {run.steps.length} recorded steps
          </summary>
          <div className="mt-2 space-y-1">
            {run.steps.map((step, index) => (
              <details key={index} className="rounded border border-border-subtle bg-surface-subtle px-2 py-1">
                <summary className="cursor-pointer font-mono text-[10px] text-ink-muted">{step.tool}</summary>
                <pre className="mt-1 max-h-52 overflow-auto whitespace-pre-wrap break-all font-mono text-[9px] leading-relaxed text-ink-faint">
                  {JSON.stringify({ input: step.input, output: step.output }, null, 2)}
                </pre>
              </details>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * The config exactly as it ran.
 *
 * Read off the run's own snapshot rather than the config row it names, which is the whole reason the
 * snapshot exists: the row may have been edited or deleted since, and a drawer that showed today's
 * spec beside last week's result would be quietly wrong in the one place it matters most.
 */
function ConfigSnapshot({ run }: { run: PlaygroundRunDetail }) {
  const spec = run.configSnapshot;
  const roster = run.result?.roster;

  return (
    <div className="space-y-3 text-[11px]">
      <div className="grid grid-cols-2 gap-2">
        <Pair label="Model" value={spec.model ?? 'deployment default'} />
        <Pair label="Reasoning" value={spec.reasoningEffort ?? 'default'} />
        <Pair label="Step budget" value={spec.maxSteps?.toString() ?? 'default'} />
        <Pair label="Confidence floor" value={spec.minConfidence?.toString() ?? 'default'} />
      </div>

      {roster && (roster.removed.length > 0 || roster.guided.length > 0) ? (
        <div className="space-y-1">
          {roster.removed.length > 0 && (
            <p className="text-[10px] text-ink-muted">
              <span className="font-medium text-ink-secondary">Removed:</span> {roster.removed.join(', ')}
            </p>
          )}
          {roster.guided.length > 0 && (
            <p className="text-[10px] text-ink-muted">
              <span className="font-medium text-ink-secondary">Guided:</span> {roster.guided.join(', ')}
            </p>
          )}
        </div>
      ) : (
        <p className="text-[10px] text-ink-faint">The full roster, unchanged.</p>
      )}

      {spec.tools.filter((tool) => tool.guidance?.trim()).map((tool) => (
        <div key={tool.name} className="rounded-lg border border-border bg-surface-subtle p-2.5">
          <code className="text-[10px] font-medium text-ink">{tool.name}</code>
          <p className="mt-1 whitespace-pre-wrap text-[10px] leading-relaxed text-ink-muted">{tool.guidance}</p>
        </div>
      ))}

      {(spec.systemPrompt || spec.userPrompt) && (
        <div className="space-y-1.5">
          {spec.systemPrompt && <PromptBlock label="System prompt" text={spec.systemPrompt} />}
          {spec.userPrompt && <PromptBlock label="User prompt" text={spec.userPrompt} />}
        </div>
      )}

      {run.result?.promptVersions && Object.keys(run.result.promptVersions).length > 0 && (
        <p className="text-[10px] text-ink-faint">
          {Object.entries(run.result.promptVersions)
            .map(([name, version]) => `${name} @ ${version}`)
            .join(' · ')}
        </p>
      )}

      {run.candidateProjects && (
        <details>
          <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
            The {run.candidateProjects.length} projects it chose between
          </summary>
          <div className="mt-1.5 space-y-0.5">
            {run.candidateProjects.map((project) => (
              <div key={project.id} className="text-[10px]" style={{ paddingLeft: `${project.depth * 10}px` }}>
                <span className="font-medium text-ink-secondary">{project.name}</span>
                {project.description ? (
                  <span className="text-ink-faint"> — {project.description}</span>
                ) : (
                  <span className="text-warning-text"> — no description, so only its pages could place anything here</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function PromptBlock({ label, text }: { label: string; text: string }) {
  return (
    <details className="rounded-lg border border-orange-300 dark:border-orange-800">
      <summary className="cursor-pointer px-2.5 py-1.5 text-[10px] font-medium text-ink">
        {label} — overridden
      </summary>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words border-t border-border-subtle px-2.5 py-2 font-mono text-[9px] leading-relaxed text-ink-muted">
        {text}
      </pre>
    </details>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface-subtle px-2.5 py-1.5">
      <p className="text-[9px] font-medium uppercase tracking-wide text-ink-faint">{label}</p>
      <p className="mt-0.5 truncate text-[11px] text-ink-secondary">{value}</p>
    </div>
  );
}

// ─── Cases ────────────────────────────────────────────────────────────────────

function CaseList({
  agent,
  cases,
  onChanged,
}: {
  agent: PlaygroundAgentInfo;
  cases: PlaygroundCase[];
  onChanged: () => void;
}) {
  const { allProjects } = useProjectTree();
  const sourceLabels = useSourceLabels();
  const [openId, setOpenId] = useState<string | null>(null);
  const open = cases.find((one) => one.id === openId) ?? null;

  if (cases.length === 0) {
    return (
      <Empty>
        No cases yet. A case is a recording of real content plus what the agent should do with it —
        the content is copied rather than referenced, because a test whose input can change under it
        is not a test.
      </Empty>
    );
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-5">
      <div className="space-y-1">
        {cases.map((one) => (
          <button
            key={one.id}
            onClick={() => setOpenId(one.id === openId ? null : one.id)}
            className={cn(
              'w-full rounded-lg border px-3 py-2 text-left transition-colors',
              one.id === openId ? 'border-orange-400 bg-surface-subtle' : 'border-border bg-card hover:bg-surface-subtle',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="truncate text-[11px] font-medium text-ink">{one.name}</span>
              {one.expectation ? <Badge variant="info">graded</Badge> : <Badge variant="muted">measured only</Badge>}
              <span className="ml-auto flex-shrink-0 text-[10px] tabular-nums text-ink-faint">
                {one.runCount} run{one.runCount === 1 ? '' : 's'} · {one.chars.toLocaleString()} chars
              </span>
            </div>
            {one.notes && <p className="mt-0.5 truncate text-[10px] text-ink-faint">{one.notes}</p>}
          </button>
        ))}
      </div>

      {open && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <h3 className="min-w-0 flex-1 truncate text-xs font-semibold text-ink">{open.name}</h3>
            <button
              onClick={async () => {
                await api.playground.deleteCase(open.id);
                setOpenId(null);
                onChanged();
              }}
              className="text-ink-faint transition-colors hover:text-danger-text"
              title="Delete this case"
            >
              <Trash2 size={12} />
            </button>
          </div>

          <p className="mb-3 text-[10px] leading-relaxed text-ink-faint">
            {open.items.length} item{open.items.length === 1 ? '' : 's'} from {formatSource(open.source, sourceLabels)}
            {open.sourceFeedItemId ? ' · lifted from a real arrival' : ''}
          </p>

          <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
            What it should do
          </h4>
          <ExpectationEditor
            projects={allProjects}
            value={open.expectation}
            onChange={async (expectation) => {
              await api.playground.updateCase(open.id, { expectation });
              onChanged();
            }}
            mode={agent.input}
          />

          <Button
            size="sm"
            variant="ghost"
            className="mt-3 w-full"
            onClick={async () => {
              await api.playground.startRuns({ agent: agent.agent, caseIds: [open.id] });
              onChanged();
            }}
          >
            <Play size={11} />
            Run this case against the shipped agent
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Configs ──────────────────────────────────────────────────────────────────

function ConfigList({ configs, onChanged }: { configs: PlaygroundConfig[]; onChanged: () => void }) {
  if (configs.length === 0) {
    return (
      <Empty>
        No saved configurations. Build one on the Bench and save it — a run made with it keeps its own
        copy, so editing it later never rewrites what already happened.
      </Empty>
    );
  }

  return (
    <div className="space-y-1">
      {configs.map((config) => (
        <div
          key={config.id}
          className="group flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[11px] font-medium text-ink">{config.name}</span>
              {config.archivedAt && <Badge variant="muted">archived</Badge>}
              {isBaselineSpec(config.spec) && <Badge variant="muted">baseline</Badge>}
            </div>
            <p className="mt-0.5 truncate text-[10px] text-ink-faint">
              {config.description || describeSpec(config.spec)}
            </p>
          </div>
          <span className="flex-shrink-0 text-[10px] tabular-nums text-ink-faint">
            {config.runCount} run{config.runCount === 1 ? '' : 's'}
          </span>
          <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              onClick={async () => {
                await api.playground.updateConfig(config.id, { archived: !config.archivedAt });
                onChanged();
              }}
              title={config.archivedAt ? 'Restore' : 'Archive'}
              className="text-ink-faint hover:text-ink"
            >
              <Archive size={11} />
            </button>
            <button
              onClick={async () => {
                await api.playground.deleteConfig(config.id);
                onChanged();
              }}
              title="Delete — the runs keep their own snapshots"
              className="text-ink-faint hover:text-danger-text"
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** One line describing what a spec changes, for a config with no description of its own. */
function describeSpec(spec: PlaygroundSpec): string {
  const parts: string[] = [];
  const off = spec.tools.filter((tool) => !tool.enabled).map((tool) => tool.name);
  const guided = spec.tools.filter((tool) => tool.guidance?.trim()).map((tool) => tool.name);
  if (off.length > 0) parts.push(`without ${off.join(', ')}`);
  if (guided.length > 0) parts.push(`guidance on ${guided.join(', ')}`);
  if (spec.systemPrompt) parts.push('system prompt overridden');
  if (spec.userPrompt) parts.push('user prompt overridden');
  if (spec.model) parts.push(spec.model);
  if (spec.minConfidence !== null) parts.push(`floor ${spec.minConfidence}`);
  if (spec.maxSteps !== null) parts.push(`${spec.maxSteps} steps`);
  return parts.length > 0 ? parts.join(' · ') : 'exactly what ships';
}

// ─── Dialogs ──────────────────────────────────────────────────────────────────

function SaveConfigDialog({
  agent,
  spec,
  onClose,
  onSaved,
}: {
  agent: PlaygroundAgentInfo;
  spec: PlaygroundSpec;
  onClose: () => void;
  onSaved: (config: PlaygroundConfig) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal title="Save this configuration" onClose={onClose}>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="text-xs" autoFocus />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={`What is this testing? — ${describeSpec(spec)}`}
        rows={3}
        className="text-[11px]"
      />
      {error && <p className="text-[10px] text-danger-text">{error}</p>}
      <Button
        variant="primary"
        className="w-full"
        disabled={busy || !name.trim()}
        onClick={async () => {
          setBusy(true);
          try {
            const saved = await api.playground.createConfig({
              agent: agent.agent,
              name: name.trim(),
              description: description.trim() || null,
              spec,
            });
            onSaved(saved);
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
        Save
      </Button>
    </Modal>
  );
}

function SaveCaseDialog({
  agent,
  items,
  expectation,
  defaultName,
  onClose,
  onSaved,
}: {
  agent: PlaygroundAgentInfo;
  items: PlaygroundCaseItem[];
  expectation: PlaygroundExpectation | null;
  defaultName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chars = useMemo(() => items.reduce((sum, item) => sum + item.content.length, 0), [items]);

  return (
    <Modal title="Save as a case" onClose={onClose}>
      <p className="text-[10px] leading-relaxed text-ink-faint">
        {chars.toLocaleString()} characters, copied rather than referenced — the case keeps working
        after the arrival it came from is edited or deleted.
      </p>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="text-xs" autoFocus />
      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="What is this case meant to catch?"
        rows={3}
        className="text-[11px]"
      />
      {error && <p className="text-[10px] text-danger-text">{error}</p>}
      <Button
        variant="primary"
        className="w-full"
        disabled={busy || !name.trim()}
        onClick={async () => {
          setBusy(true);
          try {
            await api.playground.createCase({
              agent: agent.agent,
              name: name.trim(),
              notes: notes.trim() || null,
              items,
              ...(items[0]?.feedItemId ? { sourceFeedItemId: items[0].feedItemId } : {}),
              expectation,
            });
            onSaved();
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
        Save case
      </Button>
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6" onClick={onClose}>
      <div
        className="w-full max-w-sm space-y-2.5 rounded-xl border border-border bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <h3 className="flex-1 text-xs font-semibold text-ink">{title}</h3>
          <button onClick={onClose} className="text-ink-faint hover:text-ink">
            <X size={13} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="max-w-lg px-1 py-6 text-[11px] leading-relaxed text-ink-faint">{children}</p>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 px-1 py-6 text-[11px] text-ink-faint">
      <Loader2 size={12} className="animate-spin" />
      Loading
    </div>
  );
}
